import {
  PlayerImage,
  PlayerName,
  PlayerProfile,
  PlayerSecondaryHeader,
  PlayerSubheader
} from '@library/components/player';
import { classNames, isStandard } from '@library/utils';
import { DateTime } from 'luxon';
import type { GamePlayerProjectionTrading } from '@library/types/trading';
import type { LeagueInfo } from '@library/types/leagues';
import { NumberInput } from '@library/components/inputs';
import { OddsTypeIndicator } from '@library/components/OddsTypeIndicator';
import { PromoIndicator } from '@library/components/PromoIndicator';
import { StatusIndicator } from '@library/components/StatusIndicator';
import { formatWithTimezoneChange } from '@library/utils/dateTime';
import { getDurationDisplayForLeague } from '@bo-utils/leagues';
import { useState } from 'react';

interface RankDrawerProjectionProps {
  className?: string;
  handleProjectionRankChange: ({
    targetProjectionId,
    value
  }: {
    targetProjectionId: string;
    value: string;
  }) => void;
  leagueInfo?: LeagueInfo;
  projection: GamePlayerProjectionTrading;
  showDuration?: boolean;
}

export const RankDrawerProjection = ({
  className = '',
  handleProjectionRankChange,
  leagueInfo = undefined,
  projection,
  showDuration = false
}: RankDrawerProjectionProps) => {
  const {
    boardTime,
    description,
    durationId = '',
    isPromo,
    leagueId = '',
    oddsType,
    optIn = false,
    playerImage,
    playerName,
    projectionId,
    rank,
    startTime,
    status,
    team
  } = projection;

  const durationDisplay = showDuration
    ? getDurationDisplayForLeague({ durationId, leagueId, leagueInfo })
    : '';

  const startDay = DateTime.fromISO(startTime).toLocaleString(
    DateTime.DATE_SHORT
  );
  const startTimeDisplay = formatWithTimezoneChange(
    DateTime.fromISO(startTime),
    'ccc HH:mm ZZZZ'
  );

  const [initialRank] = useState(rank);
  const teamInitials = team ?? '';
  const rankChanged = initialRank !== rank;
  const showOdds = !isStandard(oddsType);

  const handleOnProjectionRankBlur = () => {
    if (rank === undefined) {
      handleProjectionRankChange({
        targetProjectionId: projectionId,
        value: typeof initialRank === 'number' ? String(initialRank) : '0'
      });
    }
  };

  return (
    <div
      className={classNames('flex justify-between px-4 py-2', className)}
      key={projectionId}
    >
      <PlayerProfile
        className="flex items-center gap-2"
        imageUrl={playerImage}
        name={playerName}
        secondaryHeader={` vs ${description} @ ${startDay} | ${startTimeDisplay}`}
        subheader={teamInitials}
      >
        <div className="bg-soFresh-120 h-8 w-11 self-center rounded-lg px-2">
          <PlayerImage className="w-14" />
        </div>

        <div className="border-soFresh-110 flex w-max flex-col gap-1 border-r border-solid pl-1 text-xs leading-3">
          <div className="flex flex-row gap-2">
            <PlayerName className="text-soClean-100 font-medium" />
            <span className="text-soClean-160 text-xs font-normal leading-3">
              {durationDisplay}
            </span>
            <StatusIndicator
              boardTime={boardTime}
              className="self-center"
              status={status}
            />
            {showOdds && (
              <OddsTypeIndicator className="size-4" oddsType={oddsType} />
            )}
            {isPromo && <PromoIndicator className="size-4" optIn={optIn} />}
          </div>
          <div className="whitespace-nowrap text-xs font-medium">
            <PlayerSubheader className="text-soClean-130 font-bold" />
            <PlayerSecondaryHeader className="text-soClean-160" />
          </div>
        </div>
      </PlayerProfile>
      <NumberInput
        id={`projection-rank-input-${projectionId}`}
        className={classNames(
          'focus:border-msJackson bg-soFresh-120 border-soFresh-140 text-soClean-100 h-8 w-12 justify-center rounded-md p-0 text-center text-sm font-medium leading-5 tracking-tight',
          rankChanged ? '!border-msJackson' : ''
        )}
        onBlur={handleOnProjectionRankBlur}
        onValueChange={(value: string) => {
          handleProjectionRankChange({
            targetProjectionId: projectionId,
            value
          });
        }}
        value={rank ?? ''}
      />
    </div>
  );
};
